// A FIÓK DOMAINJEI — ismételhető sorok (domain · állapot), a rendszerazonosítók (`AccountIntegrationsField`)
// VIZUÁLIS és MŰKÖDÉSI MINTÁJÁRA: keretes sor, szellem-gombos törlés, körvonalas „+ Domain felvétele", és a lap
// EGYETLEN Mentés-gombja menti (user-döntés: form-oldalon a rekord ismétlődő adata NEM oldalpanelben szerkesztődik).
//
// A régi szabad szöveges „Egyéb domainek" mező utódja: ott a domainek közé CMS-azonosítók és megjegyzések
// keveredtek, és egy domain állapota nem volt kifejezhető. A FŐ domain a Weboldal mező — itt nem szerepel.
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ACCOUNT_DOMAIN_STATUSES } from '@atlas/shared';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SelectField, TextField } from '@/components/shared/form-fields';
import { Button } from '@/components/ui/button';
import { newDomainRow, type AccountFormValues } from '@/features/accounts/form';

export interface AccountDomainsFieldProps {
  form: UseFormReturn<AccountFormValues>;
  disabled?: boolean;
}

export function AccountDomainsField({
  form,
  disabled = false,
}: AccountDomainsFieldProps): JSX.Element {
  const { t } = useTranslation();
  // a `keyName` külön kulcs: a sor saját `domainId`-ja a form-értékben marad
  const array = useFieldArray({ control: form.control, name: 'domains', keyName: 'key' });
  const statusOptions = ACCOUNT_DOMAIN_STATUSES.map((s) => ({
    value: s,
    label: t(`accounts.domains.status.${s}`),
  }));
  // a lista-szintű szerver-hiba (pl. részleges mentés) — a sorokhoz nem köthető
  const listError = form.formState.errors.domains?.message;

  return (
    <div className="space-y-4">
      <p className="text-subtle text-xs">{t('accounts.domains.hint')}</p>

      {listError && (
        <p role="alert" className="text-destructive text-sm">
          {listError}
        </p>
      )}

      {array.fields.length === 0 && (
        <p className="text-muted-foreground text-sm">{t('accounts.domains.empty')}</p>
      )}

      {array.fields.length > 0 && (
        <div className="space-y-3">
          {array.fields.map((field, index) => (
            <div
              key={field.key}
              className="border-border grid grid-cols-1 gap-3 rounded-lg border p-3 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]"
            >
              <TextField<AccountFormValues>
                name={`domains.${index}.domain`}
                label={t('accounts.domains.domain')}
                placeholder={t('accounts.domains.domainPlaceholder')}
                disabled={disabled}
                required
                autoComplete="off"
                inputMode="url"
              />
              {/* A törlés az állapot mellett áll (a sor jobb alsó sarka) — mobilon sem lóg ki külön sorba. */}
              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <SelectField<AccountFormValues>
                    name={`domains.${index}.status`}
                    label={t('accounts.domains.statusLabel')}
                    options={statusOptions}
                    disabled={disabled}
                  />
                </div>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('accounts.domains.remove')}
                    onClick={() => array.remove(index)}
                  >
                    <TrashIcon className="text-destructive h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => array.append(newDomainRow())}
        >
          <PlusIcon className="h-4 w-4" />
          {t('accounts.domains.add')}
        </Button>
      )}
    </div>
  );
}
